<script setup lang="ts">
  import { computed } from "vue";
  import { useI18n } from "vue-i18n";
  import { Dialog, Button, IconWarningRound, Text, useNotification } from "@ui-kit";
  import { type Product, ResponseCode, useItemUnsetZone } from "@src/api";
  import { ProductZone } from "@src/components";
  import { useShopZoneHook } from "@src/hooks";

  const { product, zoneIds } = defineProps<{ product: Product | null; zoneIds: string[] | null }>();
  const emit = defineEmits<{ "close": [value: void]; "click:attach": [value: void] }>();

  const { shopZones } = useShopZoneHook();
  const { mutateAsync: itemUnsetZone, isPending: isPendingUnsetZone } = useItemUnsetZone();
  const { show } = useNotification();
  const { t } = useI18n();

  // TODO: hierarchy
  const item = computed(() => {
    const zones = zoneIds.map((id) => shopZones.value.byId[id]);
    const item = {
      id: product.item_id,
      article: product.article,
      img: product.image_url,
      zones: [],
    };

    zones.filter(Boolean).forEach((zone) => {
      const zone_ids = zone.hierarchy.split(".");
      let path = "";
      let location = "";

      if (zone_ids.length === 2) {
        path = shopZones.value.byId[zone_ids.at(0)].title;
        location = shopZones.value.byId[zone_ids.at(-1)].title;
      } else if (zone_ids.length > 2) {
        const path_origin = [
          shopZones.value.byId[zone_ids.at(0)].title.slice(0, 1),
          shopZones.value.byId[zone_ids.at(1)].title.slice(-2),
        ].join(" ");
        const path_other = zone_ids.slice(2, -1).map((id) => shopZones.value.byId[id].title);

        path = [path_origin, ...path_other].join(" • ");
        location = shopZones.value.byId[zone_ids.at(-1)].title;
      }

      item.zones.push({
        id: zone_ids.at(-1),
        path,
        location,
      });
    });

    return item;
  });

  const handleRelinkProduct = async () => {
    const result = await itemUnsetZone({ item_id: product.item_id, zone_ids: zoneIds });

    if (result.code === ResponseCode.OK) {
      emit("click:attach");
    } else {
      show({ title: t("Ошибка при прикреплении товара"), type: "error" });
    }
  };
</script>

<template>
  <Dialog :isOpen="Boolean(product)" :closeOnOutsideClick="false" @close="emit('close')">
    <div class="product-zoning-exist-zone-modal" data-testid="product-zoning-exist-zone-modal">
      <div class="product-zoning-exist-zone-modal__content">
        <IconWarningRound style="width: 56px; height: 56px; stroke: var(--sf-base-icon-warning)" />
        <div class="product-zoning-exist-zone-modal__title">
          <Text font="body-bold-18">{{ $t("Товар в другом месте") }}</Text>
          <Text font="body-regular-14">{{ $t("Можете перепривязать его или оставить на старом месте") }}</Text>
        </div>
        <ProductZone :zones="item.zones" :img="item.img" :article="item.article" />
      </div>
      <div class="product-zoning-exist-zone-modal__footer">
        <Button size="s" @click="handleRelinkProduct" data-testid="product-zoning-exist-zone-modal-relink-btn">
          {{ $t("Перепривязать товар") }}
        </Button>
        <Button
          size="s"
          variant="secondary"
          @click="emit('click:attach')"
          :isLoading="isPendingUnsetZone"
          data-testid="product-zoning-exist-zone-modal-attach-btn"
        >
          {{ $t("Добавить к новой зоне") }}
        </Button>
        <Button
          size="s"
          variant="secondary"
          @click="emit('close')"
          data-testid="product-zoning-exist-zone-modal-cancel-btn"
        >
          {{ $t("Не прикреплять") }}
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
  .product-zoning-exist-zone-modal {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px;
    width: 328px;
  }

  .product-zoning-exist-zone-modal__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
  }

  .product-zoning-exist-zone-modal__content :deep(.icon) path {
    stroke-width: 1px;
  }

  .product-zoning-exist-zone-modal :deep(.product-zone) {
    width: 100%;
    padding: 0;
  }

  .product-zoning-exist-zone-modal__title {
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: center;
  }

  .product-zoning-exist-zone-modal__footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .product-zoning-exist-zone-modal__footer :deep(button) {
    width: 100%;
    padding: 0;
  }
</style>
